import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {InvoiceDto} from './interfaces/InvoiceDto';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { OpportunitiesService } from './services/opportunities.service';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import moment from 'moment';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { CacheService } from './services/cache.service';
import { AuthService } from '../../authentication/service/auth.service';

@Component({
  selector: 'app-factoring-opportunities',
  templateUrl: './factoring-opportunities.component.html',
  styleUrls: ['./factoring-opportunities.component.scss']
})
export class FactoringOpportunitiesComponent {
  selectedDate = new FormControl(new Date());
  restrictedUsers = [];
  private executiveMap = {
    'cgarrido@inge.cl': 'Carlos',
    'castudillo@inge.cl': 'Carolina',
    'bcastro@inge.cl': 'Braulio'
  };
  isRestrictedUser = false;
  currentUser: string;
  currentExecutiveName: string;
  
  @ViewChild(MatPaginator, { static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true}) sort: MatSort;
  @Output() pagination = new EventEmitter<any>;
  @Output() sorting = new EventEmitter<any>;
  public dataSource: MatTableDataSource<InvoiceDto>;
  public currentURL: string;
  public displayCols = [
    "RutCliente",
    "NombreCliente",
    "RutDeudor",
    "NombreDeudor",
    "Folio",
    "MontoFactura",
    "FechaEmision",
    "EjecutivoGarantias",
    "EjecutivoFactoring"
  ];

  public invoices: InvoiceDto[];
  public page: number = 1;
  public pageSize: number;
  public totalRows: number;
  public sortColumn: string;
  public sortDirection: string;
  public loading = false;
  public selectedDays = 1;
  public hours = 24;

  constructor(
    public opportunities: OpportunitiesService,
    private cacheService: CacheService,
    private authService: AuthService
  ){
    this.dataSource = new MatTableDataSource([]);
    this.currentUser = this.authService.getEmailIdentifier() || '';
    this.isRestrictedUser = this.restrictedUsers.includes(this.currentUser);
    this.currentExecutiveName = this.executiveMap[this.currentUser] || '';
  }
  
  ngOnInit(): void {
    this.currentURL = window.location.href.split('/')[0];
    this.getOpportunities();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private getOpportunities(){
    const selectedDate = this.selectedDate.value;
    if (selectedDate) {
      const cacheKey = `opportunities_${selectedDate.toISOString()}_${this.selectedDays}`;
      const cachedData = this.cacheService.get<any>(cacheKey);

      if (cachedData) {
        this.invoices = cachedData.value;
        this.setDataSource();
        return;
      }

      this.loading = true;
      this.opportunities.getAllOpportunities(this.selectedDays, selectedDate.toISOString()).subscribe({
        next: (invoices) => {
          this.loading = false;
          this.invoices = invoices.value;
          this.setDataSource();
          this.cacheService.set(cacheKey, invoices);
        },
        error: (error) => {
          console.error('Error al obtener oportunidades:', error);
          this.loading = false;
        }
      });
    }
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    this.getOpportunities();
  }

  private setDataSource() {
    this.dataSource.data = this.invoices;
    if (this.isRestrictedUser && this.currentExecutiveName) {
      this.applyExecutiveFilter(this.currentExecutiveName);
    }
  }

  private setSorting() {
    this.sort.active = this.sortColumn;
    if(this.sortDirection)
      this.sort.direction = this.sortDirection === 'asc' ? 'asc': 'desc';
  }

  public changePagination(event: PageEvent) {
    const pagination = {
      page: event.pageIndex + 1,
      pageSize: event.pageSize
    };
    this.pagination.emit(pagination);
  }

  public changeSorting(sortState: Sort): void {
    if(sortState.direction === '') {
      this.sorting.emit(null);
      return;
    }

    const sorting =  {
      sortColumn: sortState.active,
      sortDirection: sortState.direction.toLowerCase()
    };
    this.sorting.emit(sorting);
  }

  openUrl(id) {
    window.open(this.currentURL + '/dashboards/files-clients?client=' + id);
  }

  applyFilter(event: Event, column: string) {
    const filterValue = (event.target as HTMLInputElement).value;
    const filterValueLower = filterValue.toLowerCase();
    
    if (this.isRestrictedUser && column === 'ejecutivoFactoring') {
      return;
    }
    
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const columnValue = data[column]?.toString().toLowerCase() || '';
      return columnValue.includes(filter);
    };
    
    this.dataSource.filter = filterValueLower;
  }

  getTotalInvoices(): number {
    return this.dataSource.filteredData.length;
  }

  getTotalAmount(): number {
    return this.dataSource.filteredData.reduce((total, invoice) => total + (invoice.monto || 0), 0);
  }

  getUniqueClients(): number {
    return new Set(this.dataSource.filteredData.map(invoice => invoice.rutCompletoProveedor)).size;
  }

  getUniqueDebtors(): number {
    return new Set(this.dataSource.filteredData.map(invoice => invoice.rutCompletoCliente)).size;
  }

  private applyExecutiveFilter(executiveName: string) {
    this.dataSource.filterPredicate = (data: any) => {
      const executiveFactoring = data.ejecutivoFactoring?.toLowerCase() || '';
      return executiveFactoring.includes(executiveName.toLowerCase());
    };
    this.dataSource.filter = executiveName;
  }

  isExecutiveFilterDisabled(): boolean {
    return this.isRestrictedUser;
  }
}

